<template>
    <div class="add-maintenance-container">
        <form action="" @submit.prevent="onSubmit()">
            <!-- Maintenance Type and Name Row -->
            <b-row>
                <b-col md="6">
                    <div class="form-group">
                        <label class="form-label">Maint. Window Type <span class="required">*</span></label>
                        <div class="custom-select-wrapper" :class="{ 'has-validation': isMwindowSelected }">
                            <b-form-select 
                                name="type" 
                                v-model="input.type" 
                                :options="maintenance_type_opt" 
                                :state="null" 
                                @change="validate_type" 
                                class="custom-form-select"
                                :disabled="edit_maint"
                                required>
                            </b-form-select>
                            <span v-if="isMwindowSelected" class="validation-icon">
                                <svg v-if="!invalid_mwindow_message" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="9" fill="#10B981" stroke="#10B981" stroke-width="2"/>
                                    <path d="M6 10L8.5 12.5L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="9" fill="#EF4444" stroke="#EF4444" stroke-width="2"/>
                                    <path d="M12.5 7.5L7.5 12.5M7.5 7.5L12.5 12.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </span>
                            <svg class="dropdown-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                        <b-form-invalid-feedback v-if="isMwindowSelected && invalid_mwindow_message">{{ invalid_mwindow_message }}</b-form-invalid-feedback>
                    </div>
                </b-col>
                <b-col md="6">
                    <div class="form-group">
                        <label class="form-label">Friendly Name <span class="required">*</span></label>
                        <div class="custom-input-wrapper" :class="{ 'has-validation': isNameEntered }">
                            <b-form-input 
                                type="text" 
                                name="name" 
                                v-model="input.name" 
                                :state="null"  
                                @input="validateName"  
                                maxlength="50" 
                                placeholder="Enter Name" 
                                class="custom-form-input"
                                required>
                            </b-form-input>
                            <span v-if="isNameEntered" class="validation-icon">
                                <svg v-if="!invalid_fname_message" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="9" fill="#10B981" stroke="#10B981" stroke-width="2"/>
                                    <path d="M6 10L8.5 12.5L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="9" fill="#EF4444" stroke="#EF4444" stroke-width="2"/>
                                    <path d="M12.5 7.5L7.5 12.5M7.5 7.5L12.5 12.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </span>
                        </div>
                        <b-form-invalid-feedback v-if="isNameEntered && invalid_fname_message">{{ invalid_fname_message }}</b-form-invalid-feedback>
                    </div>
                </b-col>
            </b-row>

            <!-- Row 2: Time Zone and Start Time -->
            <b-row v-if="input.type">
                <b-col md="6">
                    <div class="form-group">
                        <label class="form-label">Time Zone</label>
                        <div class="custom-select-wrapper">
                            <b-form-select 
                                name="timezone" 
                                v-model="input.timezone" 
                                :options="timezone_list" 
                                class="custom-form-select"
                            >
                            </b-form-select>
                            <svg class="dropdown-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>
                </b-col>
                <b-col md="6">
                    <div class="form-group">
                        <label class="form-label">Maint. Start Time <span class="required">*</span></label>
                        <div class="custom-input-wrapper time-picker-wrapper" :class="{ 'has-validation': isTimeValid || isTimeInvalid }">
                            <vue-timepicker 
                                v-model="input.startTime" 
                                format="HH:mm" 
                                @input="validateStartTime"
                                :input-class="'custom-time-input'"
                                hide-clear-button
                            >
                            </vue-timepicker>
                            <span v-if="isTimeValid || isTimeInvalid" class="validation-icon">
                                <svg v-if="isTimeValid" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="9" fill="#10B981" stroke="#10B981" stroke-width="2"/>
                                    <path d="M6 10L8.5 12.5L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="9" fill="#EF4444" stroke="#EF4444" stroke-width="2"/>
                                    <path d="M12.5 7.5L7.5 12.5M7.5 7.5L12.5 12.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </span>
                        </div>
                        <b-form-invalid-feedback v-if="isTimeInvalid" class="d-block">{{ invalid_startTime_message }}</b-form-invalid-feedback>
                    </div>
                </b-col>
            </b-row>

            <!-- Row 3: Duration and Value (for Weekly/Monthly) or Start Date (for Once) -->
            <b-row v-if="input.type">
                <b-col md="6">
                    <div class="form-group">
                        <label class="form-label">Maint. Duration <span class="required">*</span> (in minutes)</label>
                        <div class="duration-container">
                            <div class="custom-input-wrapper duration-wrapper" :class="{ 'has-validation': isDurationEntered }">
                                <button type="button" class="duration-btn minus" @click="decrementDuration" :disabled="input.duration <= 1">
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5 10H15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                    </svg>
                                </button>
                                <b-form-input 
                                    type="number" 
                                    v-model="input.duration" 
                                    :state="null"  
                                    @input="validateDuration"   
                                    min="1" 
                                    class="custom-form-input duration-input"
                                    required>
                                </b-form-input>
                                <span class="duration-label">min.</span>
                                <button type="button" class="duration-btn plus" @click="incrementDuration">
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10 5V15M5 10H15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                    </svg>
                                </button>
                                <span v-if="isDurationEntered" class="validation-icon">
                                    <svg v-if="!invalid_duration_message" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#10B981" stroke="#10B981" stroke-width="2"/>
                                        <path d="M6 10L8.5 12.5L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#EF4444" stroke="#EF4444" stroke-width="2"/>
                                        <path d="M12.5 7.5L7.5 12.5M7.5 7.5L12.5 12.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </span>
                            </div>
                            <button type="button" class="reset-btn" @click="resetForm">
                                Reset
                            </button>
                        </div>
                        <b-form-invalid-feedback v-if="isDurationEntered && invalid_duration_message">{{ invalid_duration_message }}</b-form-invalid-feedback>
                    </div>
                </b-col>
                <b-col md="6" v-if="input.type === 3 || input.type === 4">
                    <div class="form-group">
                        <label class="form-label">Value <span class="required">*</span></label>
                        <div class="custom-select-wrapper day-select" @click="toggleDayDropdown">
                            <span class="select-placeholder">{{ daySelectText }}</span>
                            <svg class="dropdown-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            
                            <!-- Day Dropdown -->
                            <div v-if="showDayDropdown" class="day-dropdown" @click.stop>
                                <div class="day-search">
                                    <input 
                                        type="text" 
                                        v-model="daySearchQuery" 
                                        :placeholder="input.type === 3 ? 'Search week days...' : 'Search month days...'"
                                        class="day-search-input"
                                        @click.stop
                                    />
                                </div>
                                <div class="day-options">
                                    <div 
                                        v-for="day in filteredDays" 
                                        :key="day.value"
                                        class="day-option"
                                        :class="{ 'selected': isDaySelected(day) }"
                                        @click.stop="toggleDay(day)"
                                    >
                                        <input 
                                            type="checkbox" 
                                            :checked="isDaySelected(day)"
                                            class="day-checkbox"
                                        />
                                        <span>{{ day.text }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <b-form-invalid-feedback v-if="isValueInvalid" class="d-block">{{ input.type === 3 ? invalid_weekDay_message : invalid_monthDay_message }}</b-form-invalid-feedback>
                    </div>
                </b-col>
                <b-col md="6" v-if="input.type === 1">
                    <div class="form-group">
                        <label class="form-label">Maint. Start Date <span class="required">*</span></label>
                        <div class="custom-input-wrapper date-picker-wrapper" :class="{ 'has-validation': isDateSelected }">
                            <!-- <img src="@/assets/icons/calendar-icon.svg" alt="Calendar" class="date-icon" /> -->
                            <b-form-datepicker 
                                v-model="input.datetime" 
                                :state="null"  
                                @input="validateDate" 
                                :date-disabled-fn="dateDisabled" 
                                class="custom-date-picker"
                                required>
                            </b-form-datepicker>
                            <span v-if="isDateSelected" class="validation-icon">
                                <svg v-if="!invalid_date_message" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="9" fill="#10B981" stroke="#10B981" stroke-width="2"/>
                                    <path d="M6 10L8.5 12.5L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="9" fill="#EF4444" stroke="#EF4444" stroke-width="2"/>
                                    <path d="M12.5 7.5L7.5 12.5M7.5 7.5L12.5 12.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </span>
                        </div>
                        <b-form-invalid-feedback v-if="isDateSelected && invalid_date_message">{{ invalid_date_message }}</b-form-invalid-feedback>
                    </div>
                </b-col>
            </b-row>

            <!-- Selected Days Display -->
            <div v-if="input.value.length > 0 && (input.type === 3 || input.type === 4)" class="form-group">
                <div class="selected-days-header">
                    <span class="selected-days-title">Selected {{ input.type === 3 ? 'Week Days' : 'Month Days' }}</span>
                    <span class="selected-days-count">{{ input.value.length }}</span>
                </div>
                <div class="selected-days-tags">
                    <span 
                        v-for="day in input.value" 
                        :key="day.value"
                        class="day-tag"
                    >
                        <span class="day-tag-dot"></span>
                        {{ day.text }}
                        <svg @click="removeDay(day)" class="tag-remove" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </span>
                </div>
            </div>


            <!-- Submit Button -->
            <div class="form-actions">
                <button 
                    type="submit" 
                    class="create-maintenance-btn" 
                    :disabled="disable_submit || !canSubmit"
                    :class="{ 'disabled': disable_submit || !canSubmit }"
                >
                    {{edit_maint ? 'Save Changes' : 'Add Maintenance Window Now'}}
                </button>
            </div>
        </form>
    </div>
</template>


<script>
import MaintenanceController from "../../../services/maintenanceController";
import Multiselect from 'vue-multiselect';
import VueTimepicker from 'vue2-timepicker';
import 'vue2-timepicker/dist/VueTimepicker.css';
export default{
    name : "AddMaintenance",
    components:{Multiselect,VueTimepicker},
    props:{add_maint_modal:Boolean,edit_maint:Boolean,maint_data:Object},
    data(){
        return {
            timezone_list: Intl.supportedValuesOf('timeZone'),
            disable_submit:false,
            input:{type:null,name:'',startTime:'',duration:1,value:[],datetime:'',timezone:Intl.DateTimeFormat().resolvedOptions().timeZone},
            maintenance_type_opt:[
              { value: null, text:'Please Select' },
              { value: 1, text:'Once' },
              { value: 2, text:'Daily' },
              { value: 3, text:'Weekly' },
              { value: 4, text:'Monthly' },
            ],
            weekly_opt:[
                { value: 1, text:'Monday' },
                { value: 2, text:'Tuesday' },
                { value: 3, text:'Wednesday' },
                { value: 4, text:'Thursday' },
                { value: 5, text:'Friday' },
                { value: 6, text:'Saturday' },
                { value: 0, text:'Sunday' },
            ],
            monthly_opt:[
                { value: 1, text:'1st' },
                { value: 2, text:'2nd' },
                { value: 3, text:'3rd' },
                { value: 4, text:'4th' },
                { value: 5, text:'5th' },
                { value: 6, text:'6th' },
                { value: 7, text:'7th' },
                { value: 8, text:'8th' },
                { value: 9, text:'9th' },
                { value: 10, text:'10th' },
                { value: 11, text:'11th' },
                { value: 12, text:'12th' },
                { value: 13, text:'13th' },
                { value: 14, text:'14th' },
                { value: 15, text:'15th' },
                { value: 16, text:'16th' },
                { value: 17, text:'17th' },
                { value: 18, text:'18th' },
                { value: 19, text:'19th' },
                { value: 20, text:'20th' },
                { value: 21, text:'21st' },
                { value: 22, text:'22nd' },
                { value: 23, text:'23rd' },
                { value: 24, text:'24th' },
                { value: 25, text:'25th' },
                { value: 26, text:'26th' },
                { value: 27, text:'27th' },
                { value: 28, text:'28th' },
                { value: 29, text:'29th' },
                { value: 30, text:'30th' },
                { value: 31, text:'31st' },
            ],
            payload:{type:null,name:'',startTime:'',duration:1,value:[]},
            invalid_fname_message: 'The name field is required',
            invalid_mwindow_message: 'Please select maintenance window type',
            isMwindowSelected: false,
            isNameEntered: false,
            isDurationEntered: false,
            invalid_duration_message: 'The duration field is required',
            isDateSelected: false,
            invalid_date_message: 'Please select any date',
            invalid_weekDay_message: 'Please select atleast one week day',
            invalid_monthDay_message: 'Please select atleast one month day',
            invalid_startTime_message: 'Please select valid start time',
            isValueValid: false,
            isValueInvalid: false,
            isTimeValid: false,
            isTimeInvalid: false,
            showDayDropdown: false,
            daySearchQuery: '',
            isInitializing: false
        }
    },
    computed:{
        validate_name(){
            if(this.input.name == ''){return false;}
            else{return true;}
        },
        canSubmit() {
            if(!this.input.type || !this.input.name) return false;
            if(this.input.type === 1 && !this.input.datetime) return false;
            if((this.input.type === 3 || this.input.type === 4) && this.input.value.length === 0) return false;
            return true;
        },
        daySelectText() {
            if(this.input.value.length === 0) {
                return 'Search and Add';
            }
            const type = this.input.type === 3 ? 'day' : 'date';
            return `${this.input.value.length} ${type}${this.input.value.length > 1 ? 's' : ''} selected`;
        },
        filteredDays() {
            const options = this.input.type === 3 ? this.weekly_opt : this.monthly_opt;
            if(!this.daySearchQuery) {
                return options;
            }
            return options.filter(day => 
                day.text.toLowerCase().includes(this.daySearchQuery.toLowerCase())
            );
        }
    },
    mounted(){
        this.input.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        this.input.startTime = this.getCurrentDateTime()
        
        // Handle edit mode
        if(this.edit_maint && this.maint_data){
            this.isInitializing = true;
            
            // Set validation states
            this.isMwindowSelected = true;
            this.invalid_mwindow_message = '';
            this.isNameEntered = true;
            this.invalid_fname_message = '';
            this.isDurationEntered = true;
            this.invalid_duration_message = '';
            this.isDateSelected = true;
            this.invalid_date_message = '';

            // Copy basic data
            this.input.name = this.maint_data.name;
            this.input.startTime = this.maint_data.startTime;
            this.input.duration = this.maint_data.duration;
            this.input._id = this.maint_data._id;
            if(this.maint_data.timezone){
                this.input.timezone = this.maint_data.timezone;
            }
            
            // Set type
            this.input.type = this.maint_data.type;
            
            // Handle type-specific values
            if(this.maint_data.type === 1){
                this.input.datetime = this.changeDateFormate(this.maint_data.value[0]);
            }
            else if(this.maint_data.type === 3){
                // For weekly - recreate the objects
                const weeklyValues = [];
                this.maint_data.value.forEach(val => {
                    const weekDay = this.weekly_opt.find(opt => opt.value === val);
                    if(weekDay) {
                        weeklyValues.push({...weekDay});
                    }
                });
                // Use Vue.set to ensure reactivity
                this.$set(this.input, 'value', weeklyValues);
            }
            else if(this.maint_data.type === 4){
                // For monthly - recreate the objects
                const monthlyValues = [];
                this.maint_data.value.forEach(val => {
                    const monthDay = this.monthly_opt.find(opt => opt.value === val);
                    if(monthDay) {
                        monthlyValues.push({...monthDay});
                    }
                });
                // Use Vue.set to ensure reactivity
                this.$set(this.input, 'value', monthlyValues);
            }
            
            this.$nextTick(() => {
                this.isInitializing = false;
            });
        }
        // Close dropdown when clicking outside
        document.addEventListener('click', this.closeDayDropdownOnClickOutside);
        // Reposition dropdown on scroll
        window.addEventListener('scroll', this.handleScroll, true);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeDayDropdownOnClickOutside);
        window.removeEventListener('scroll', this.handleScroll, true);
    },
    watch: {
        'input.type': function(newVal) {
            if(newVal && !this.edit_maint && !this.isInitializing) {
                this.input.value = [];
                this.isValueValid = false;
                this.isValueInvalid = false;
            }
        },
        maint_data: {
            handler: function(newVal) {
                if(newVal && this.edit_maint) {
                    console.log('Maintenance data received:', newVal);
                    this.loadEditData();
                }
            },
            immediate: true,
            deep: true
        }
    },
    methods:{
        validateName(name){
            this.isNameEntered = true;
            name = name ? name : this.input.name

            if (!name || name.trim() === '') {
                this.invalid_fname_message = 'The name field is required';
                return false;
            }

            let regexp = /^(?!\s*$)[a-zA-Z0-9 ._-]+$/;
            if (regexp.test(name)) {
                this.invalid_fname_message = '';
                return true;
            } 
            else{ 
                this.invalid_fname_message = 'Please enter valid friendly name';
                return false; 
            }
        },
        dateDisabled(ymd, date) {
            let today = new Date();
            today.setHours(0, 0, 0, 0); // Remove time part to compare only dates
            return date < today; // Disable past dates
        },
        changeDateFormate(utcDateTime){
            // Create a Date object from the UTC date-time string
            let dateTime = new Date(utcDateTime);
            // Extract day, month, year, hours, and minutes in local time
            let day = String(dateTime.getDate()).padStart(2, "0");
            let month = String(dateTime.getMonth() + 1).padStart(2, "0");
            let year = dateTime.getFullYear();
     
            // Format the components into the desired format
            let formattedDateTime = `${year}-${month}-${day}`;
            return formattedDateTime
        },
        getCurrentDateTime() {
            const now = new Date();
            const timeString = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            return timeString;
        },
       async onSubmit(){
        if(this.input.type === 1 ){
            if(this.input.datetime == null || this.input.datetime == ''){
                this.$toasted.show("Please select any date", {theme: 'outline',position: "bottom-center", type: 'error', duration: 2000});
                return
            }
        }
        if(this.input.type === 3 || this.input.type === 4){
            if(this.input.value.length === 0){
                this.$toasted.show("Please select atleast one value", {theme: 'outline',position: "bottom-center", type: 'error', duration: 2000});
                return
            }
        }
        this.disable_submit = true
        var userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        switch(this.input.type){
            case 1:
                this.payload = {"type": this.input.type,"name": this.input.name,"startTime": this.input.startTime,"duration": parseInt(this.input.duration),"timezone": this.input.timezone}
                // let t = new Date(this.input.datetime).toISOString()
                this.payload.value = []
                this.payload.value.push(this.input.datetime)
                break;
            case 2:
                this.input.startTime = this.input.startTime
                this.payload = {"type": this.input.type,"name": this.input.name,"startTime": this.input.startTime,"duration": parseInt(this.input.duration),"value": [],"timezone": this.input.timezone}
                break;
            case 3:
            case 4:
                this.input.startTime = this.input.startTime
                this.payload = {"type": this.input.type,"name": this.input.name,"startTime": this.input.startTime,"duration": parseInt(this.input.duration),"value":[],"timezone": this.input.timezone}
                this.input.value.forEach((element) => {
                    this.payload.value.push(element.value)
                });
                break;
        }
        var response
                if(this.edit_maint){
                     var p = { "mwid": this.input._id, "changePayload":this.payload }
                     response= await MaintenanceController.editMaintenance(p)
                }
                else{
                    response= await MaintenanceController.addMaintenance(this.payload)
                    
                }
                if(response.data){
                        this.$toasted.show(response.message, {theme: 'outline',position: "bottom-center", type: 'success', duration: 2000});
                        this.$emit('maintmodal')  
                } else{
                    this.$toasted.show(response.message, {theme: 'outline',position: "bottom-center", type: 'error', duration: 2000});
                }
        this.disable_submit = false
        },
        validate_type(){
            this.isMwindowSelected = true
            if(!this.input.type){
                this.invalid_mwindow_message = 'Please select maintenance window type'
            }
            else{
                this.invalid_mwindow_message = ''
            }
        },
        validateDuration(){
            this.isDurationEntered = true;
            if(this.input.duration < 1){
                this.invalid_duration_message = 'Please enter valid duration'
            }
            else{
                this.invalid_duration_message = ''
            }
        },
        validateDate(){
            this.isDateSelected = true;
            if(!this.input.datetime){
                this.invalid_date_message = 'Please select any date'
            }
            else{
                this.invalid_date_message = ''
            }
        },
        validateSelectedWeek(){
            if(this.input.type === 3) {
                if(!this.input.value || this.input.value.length === 0){
                    this.isValueInvalid = true
                    this.isValueValid = false
                    this.invalid_value_message = 'Please select atleast one week day'
                }
                else{
                    this.isValueInvalid = false
                    this.isValueValid = true
                    this.invalid_value_message = ''
                }
            }
        },
        validateSelectedMonth(){
            if(this.input.type === 4) {
                if(!this.input.value || this.input.value.length === 0){
                    this.isValueInvalid = true
                    this.isValueValid = false
                    this.invalid_value_message = 'Please select atleast one month day'
                }
                else{
                    this.isValueInvalid = false
                    this.isValueValid = true
                    this.invalid_value_message = ''
                }
            }
        },
        validateStartTime(){
            if(!this.input.startTime || this.input.startTime == null){
                this.isTimeInvalid = true
                this.isTimeValid = false
                this.invalid_startTime_message = 'Please select valid start time'
            }
            else{
                this.isTimeInvalid = false
                this.isTimeValid = true
                this.invalid_startTime_message = ''
            }
        },
        incrementDuration() {
            this.input.duration = parseInt(this.input.duration) + 1;
            this.validateDuration();
        },
        decrementDuration() {
            if(this.input.duration > 1) {
                this.input.duration = parseInt(this.input.duration) - 1;
                this.validateDuration();
            }
        },
        toggleDayDropdown() {
            this.showDayDropdown = !this.showDayDropdown;
            if(this.showDayDropdown) {
                this.daySearchQuery = '';
                this.$nextTick(() => {
                    this.positionDropdown();
                });
            }
        },
        positionDropdown() {
            const selectEl = document.querySelector('.day-select');
            const dropdownEl = document.querySelector('.day-dropdown');
            if (selectEl && dropdownEl) {
                const rect = selectEl.getBoundingClientRect();
                dropdownEl.style.top = (rect.bottom + 4) + 'px';
                dropdownEl.style.left = rect.left + 'px';
                dropdownEl.style.width = rect.width + 'px';
            }
        },
        closeDayDropdownOnClickOutside(event) {
            if (!event.target.closest('.day-select')) {
                this.showDayDropdown = false;
            }
        },
        handleScroll() {
            if (this.showDayDropdown) {
                this.positionDropdown();
            }
        },
        isDaySelected(day) {
            return this.input.value.some(d => d.value === day.value);
        },
        toggleDay(day) {
            const index = this.input.value.findIndex(d => d.value === day.value);
            if(index > -1) {
                this.input.value.splice(index, 1);
            } else {
                this.input.value.push(day);
            }
            if(this.input.type === 3) {
                this.validateSelectedWeek();
            } else if(this.input.type === 4) {
                this.validateSelectedMonth();
            }
        },
        removeDay(day) {
            const index = this.input.value.findIndex(d => d.value === day.value);
            if(index > -1) {
                this.input.value.splice(index, 1);
            }
            if(this.input.type === 3) {
                this.validateSelectedWeek();
            } else if(this.input.type === 4) {
                this.validateSelectedMonth();
            }
        },
        resetForm() {
            this.input = {
                type: null,
                name: '',
                startTime: this.getCurrentDateTime(),
                duration: 1,
                value: [],
                datetime: '',
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            };
            this.isNameEntered = false;
            this.isMwindowSelected = false;
            this.isDurationEntered = false;
            this.isDateSelected = false;
            this.isTimeValid = false;
            this.isTimeInvalid = false;
            this.isValueValid = false;
            this.isValueInvalid = false;
        },
        loadEditData() {
            if(!this.maint_data) return;
            
            console.log('Loading edit data - type:', this.maint_data.type, 'values:', this.maint_data.value);
            
            // Set validation states
            this.isMwindowSelected = true;
            this.invalid_mwindow_message = '';
            this.isNameEntered = true;
            this.invalid_fname_message = '';
            this.isDurationEntered = true;
            this.invalid_duration_message = '';
            this.isDateSelected = true;
            this.invalid_date_message = '';

            // Copy basic data
            this.input.name = this.maint_data.name;
            this.input.startTime = this.maint_data.startTime;
            this.input.duration = this.maint_data.duration;
            this.input._id = this.maint_data._id;
            this.input.type = this.maint_data.type;
            
            if(this.maint_data.timezone){
                this.input.timezone = this.maint_data.timezone;
            }
            
            // Handle type-specific values
            if(this.maint_data.type === 1){
                this.input.datetime = this.changeDateFormate(this.maint_data.value[0]);
            }
            else if(this.maint_data.type === 3){
                // For weekly
                const weeklyValues = [];
                if(Array.isArray(this.maint_data.value)) {
                    this.maint_data.value.forEach(val => {
                        const weekDay = this.weekly_opt.find(opt => opt.value === val);
                        if(weekDay) {
                            weeklyValues.push({...weekDay});
                        }
                    });
                }
                this.input.value = weeklyValues;
                console.log('Set weekly values:', this.input.value);
            }
            else if(this.maint_data.type === 4){
                // For monthly
                const monthlyValues = [];
                if(Array.isArray(this.maint_data.value)) {
                    this.maint_data.value.forEach(val => {
                        const monthDay = this.monthly_opt.find(opt => opt.value === val);
                        if(monthDay) {
                            monthlyValues.push({...monthDay});
                        }
                    });
                }
                this.input.value = monthlyValues;
                console.log('Set monthly values:', this.input.value);
            }
        }
    }
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.add-maintenance-container {
  width: 100%;
}

/* Form Group */
.form-group {
  margin-bottom: 30px !important;
}

.form-label {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #3A3A3A;
  margin-bottom: 10px;
  margin-top: 8px;
  display: block;
}

.required {
  color: #E70000;
  margin-left: 2px;
}

/* Custom Input Wrapper */
.custom-input-wrapper {
  background: transparent;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  padding: 0 12px;
  height: 48px;
  display: flex;
  align-items: center;
  position: relative;
}

/* Disabled input wrapper */
.custom-input-wrapper:has(.custom-form-input:disabled) {
  background: #F3F4F6;
}

.custom-input-wrapper.has-validation {
  padding-right: 40px;
}

/* Custom Select Wrapper */
.custom-select-wrapper {
  position: relative;
  background: transparent;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  padding: 0 12px;
  height: 48px;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: border-color 0.2s ease;
}

.custom-select-wrapper:hover {
  border-color: #B3D1F8;
}

/* Disabled select wrapper */
.custom-select-wrapper:has(.custom-form-select:disabled) {
  background: #F3F4F6;
  cursor: not-allowed;
}

.custom-select-wrapper:has(.custom-form-select:disabled):hover {
  border-color: #D2E3FB;
}

.custom-select-wrapper:has(.custom-form-select:disabled) .custom-form-select {
  cursor: not-allowed;
}

.custom-select-wrapper.has-validation {
  padding-right: 60px;
}

.custom-form-select {
  border: none !important;
  background: transparent !important;
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #3A3A3A;
  padding: 2px 40px 0 10px !important;
  height: auto;
  width: 100%;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border-radius: 0 !important;
  box-shadow: none !important;
  cursor: pointer;
}

.custom-form-select:focus {
  box-shadow: none !important;
  outline: none !important;
  background: transparent !important;
  border: none !important;
}

.custom-form-select option {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 16px;
}

.dropdown-arrow {
  position: absolute;
  right: 16px;
  pointer-events: none;
  transition: transform 0.2s ease;
  z-index: 1;
}

/* Custom Form Input */
.custom-form-input {
  border: none !important;
  background-color: transparent !important;
  background: transparent !important;
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #3A3A3A;
  padding: 0 !important;
  height: auto;
  border-radius: 0 !important;
  box-shadow: none !important;
  -webkit-appearance: none !important;
}

.custom-form-input:focus {
  box-shadow: none !important;
  outline: none !important;
  background-color: transparent !important;
  background: transparent !important;
  border: none !important;
}

.custom-form-input::placeholder {
  color: #3A3A3A;
  opacity: 0.5;
}

/* Override Bootstrap default backgrounds */
.custom-input-wrapper .form-control {
  background-color: transparent !important;
  background-image: none !important;
}

/* Force transparent background on all form controls */
.custom-input-wrapper input.form-control,
.custom-input-wrapper input.form-control:focus,
.custom-input-wrapper input.form-control:active {
  background-color: transparent !important;
  background-image: none !important;
  -webkit-box-shadow: none !important;
  box-shadow: none !important;
}

/* Time Picker Wrapper */
.time-picker-wrapper {
  padding: 0;
}

.time-picker-wrapper >>> .vue__time-picker {
  width: 100%;
  border: none;
}

.time-picker-wrapper >>> .vue__time-picker input.display-time {
  border: none !important;
  border-left: #D2E3FB 1px solid !important;
  border-bottom-left-radius: 12px !important;
  border-top-left-radius: 12px !important;
  background: transparent !important;
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #3A3A3A;
  padding: 12px 12px 12px 10px;
  height: 48px;
  border-radius: 0;
}

.time-picker-wrapper >>> .vue__time-picker .dropdown {
  top: 50px;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

/* Ensure the input wrapper doesn't lose its border */
.time-picker-wrapper >>> .vue__time-picker.active input.display-time {
  border: none !important;
}

/* Maintain the wrapper border when dropdown is open */
.custom-input-wrapper:focus-within {
  border-color: #D2E3FB;
}

/* Duration Container */
.duration-container {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

/* Duration Wrapper */
.duration-wrapper {
  gap: 8px;
  padding: 0 8px;
  flex: 1;
}

.duration-wrapper.has-validation {
  padding-right: 40px;
}

.duration-btn {
  background: transparent;
  border: none;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #3A3A3A;
  transition: all 0.2s ease;
  border-radius: 6px;
}

.duration-btn:hover:not(:disabled) {
  background: #F3F4F6;
}

.duration-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.duration-input {
  text-align: center;
  max-width: 80px;
  padding: 0 8px !important;
}

/* Remove spinner arrows from number input */
.duration-input::-webkit-inner-spin-button,
.duration-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.duration-input[type=number] {
  -moz-appearance: textfield;
}

.duration-label {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 16px;
  color: #3A3A3A;
  margin-left: 4px;
}

/* Reset Button (inline with duration) */
.reset-btn {
  background: transparent;
  border: 1px solid #D0D5DD;
  border-radius: 12px;
  padding: 12px 24px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #344054;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 48px;
  white-space: nowrap;
}

.reset-btn:hover {
  background: #F9FAFB;
  border-color: #98A2B3;
}

/* Date Picker Wrapper */
.date-picker-wrapper {
  padding: 0px;
}

.date-picker-wrapper >>> .b-form-datepicker {
  border: none !important;
}

.date-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  opacity: 0.6;
}

.custom-date-picker >>> .form-control {
  border: none !important;
  background: transparent !important;
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #3A3A3A;
  padding: 10px 0px !important;
  height: auto;
}

/* Day Selector */
.day-select {
  cursor: pointer;
  position: relative;
}

.select-placeholder {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 16px;
  color: #3A3A3A;
  opacity: 0.6;
  flex: 1;
}

/* Day Dropdown */
.day-dropdown {
  position: fixed;
  background: #FFFFFF;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  z-index: 9999;
  max-height: 300px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 300px;
}

.day-search {
  padding: 12px;
  border-bottom: 1px solid #E5E7EB;
}

.day-search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #D2E3FB;
  border-radius: 8px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  outline: none;
}

.day-search-input:focus {
  border-color: #3B82F6;
}

.day-options {
  overflow-y: auto;
  max-height: 240px;
}

.day-option {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  gap: 12px;
}

.day-option:hover {
  background-color: #F9FAFB;
}

.day-option.selected {
  background-color: #F3F4F6;
}

.day-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

/* Selected Days Section */
.selected-days-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0px 12px 0px;
}

.selected-days-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: #3A3A3A;
}

.selected-days-count {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: #3A3A3A;
}

.selected-days-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.day-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(41, 51, 64, 0.15);
  border-radius: 20px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #3A3A3A;
  position: relative;
}

.day-tag-dot {
  width: 6px;
  height: 6px;
  background-color: #6B7280;
  border-radius: 50%;
}

.tag-remove {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.tag-remove:hover {
  transform: scale(1.1);
}

/* Validation Icon */
.validation-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.validation-icon svg {
  width: 20px;
  height: 20px;
}

/* Select validation icon */
.custom-select-wrapper .validation-icon {
  right: 50px;
}


/* Form Actions */
.form-actions {
  display: flex;
  justify-content: center;
  padding-top: 0;
  margin-top: 40px;
}

/* Create Maintenance Button */
.create-maintenance-btn {
  background: #2FB234;
  border: none;
  border-radius: 12px;
  padding: 12px 40px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #FFFFFF;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  height: 48px;
}

.create-maintenance-btn:hover:not(:disabled) {
  background: #28A02D;
  transform: translateY(-1px);
}

.create-maintenance-btn.disabled,
.create-maintenance-btn:disabled {
  background: #7D8699;
  cursor: not-allowed;
  opacity: 1;
  transform: none;
}

/* Invalid Feedback */
.invalid-feedback {
  display: none;
}

.invalid-feedback.d-block {
  display: block !important;
  color: #EF4444;
  font-size: 14px;
  margin-top: 6px;
}

/* Responsive */
@media screen and (max-width: 768px) {
  .create-maintenance-btn {
    width: 100%;
  }
}
</style>